import * as React from 'react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Card, Container, Typography, Modal } from '@mui/material';
import { styled } from '@mui/material/styles';

/* ────────────────────────────────────────────────────────────
   1) KLEUREN  huisstijl uit de voorbeelden
   ──────────────────────────────────────────────────────────── */
const COLORS = {
  bgFrom: '#3A1838',
  bgMid: '#50244F',
  bgTo: '#341631',
  glowPurple: 'rgba(150, 80, 150, 0.45)',
  glowOrange: 'rgba(245, 135, 40, 0.14)',
  panelBorder: 'rgba(255,255,255,0.07)',
  orange: '#F5871F',
  orangeSoft: 'rgba(245,135,31,0.16)',
  orangeBorder: 'rgba(245,135,31,0.55)',
  cardBg: 'rgba(255,255,255,0.05)',
  cardBgHover: 'rgba(255,255,255,0.09)',
  cardBorder: 'rgba(255,255,255,0.10)',
  imgPanelBg: 'rgba(255,255,255,0.06)',
  imgPanelBorder: 'rgba(255,255,255,0.08)',
  white: '#FFFFFF',
  subtext: 'rgba(255,255,255,0.68)',
  listText: 'rgba(255,255,255,0.85)',
  pageBg: '#F4F4F6',
  // Light-secties (Onze cijfers + FAQ)
  deepPurple: '#50244F',
  orangeInk: '#E0760E',
  lightCardBg: '#FFFFFF',
  lightCardBorder: 'rgba(80,36,79,0.07)',
  lightDivider: 'rgba(80,36,79,0.15)',
  lightTextMuted: '#6F6F6F',
  // 'Meer van Athena' (licht, lavendel->perzik). Eigen tinten zodat de
  // donkere secties ongemoeid blijven.
  familieHeading2: '#3A1838',
  familieSubtext: '#6B5E6A',
  familieCardBg: 'rgba(255,255,255,0.72)',
  familieCardBgHover: 'rgba(255,255,255,0.95)',
  familieCardBorder: 'rgba(80,36,79,0.10)',
  familieImgBg: 'rgba(80,36,79,0.05)',
  familieImgBorder: 'rgba(80,36,79,0.08)',
  // Modern groen (positiviteit) voor slagings-/premium-accenten.
  green: '#22A565',
  greenInk: '#1B8B54',
};

/* ────────────────────────────────────────────────────────────
   2) AFBEELDINGEN + VIDEO
   ──────────────────────────────────────────────────────────── */
const IMAGES = {
  // AthenaSummary-boekjes ('Abonnement 2025/2026'). Geverifieerd op de site.
  summaryBooks: 'https://admin.athenasummary.nl/media/wysiwyg/Frame.png',
  // AthenaSummary-boekjes ook gebruikt in 'Onze producten' (zelfde beeld).
  // Boekjes-mockup ('Abonnement 2025/2026'). Lokaal bestand in de public-map,
  // zodat we niet afhankelijk zijn van de externe admin-server.
  productBooks: '/Samenvatting-mockup-NL.png',
  // Teamfoto (intro-blok). Bestand 'Teamfoto' in de public-map van je project.
  // Vite serveert public-bestanden vanaf de root, dus verwijs met een pad dat
  // begint met '/'. Klopt de extensie niet? Pas '.jpg' aan naar .jpeg of .png.
  team: '/Teamfoto.jpg',
  // Let op: de AthenaDocs Premium dashboard-mockup bestaat NIET als afbeelding;
  // op de site is dat HTML/CSS. We bouwen 'm daarom in code na (DocsMockup).
};

const VIDEO = {
  youtubeId: '5ab6G2rh4jw',
  thumb: 'https://i.ytimg.com/vi/5ab6G2rh4jw/maxresdefault.jpg',
  label: '2 min · bekijk de reis',
};

/* ────────────────────────────────────────────────────────────
   3) INHOUD
   ──────────────────────────────────────────────────────────── */
const FAMILIE_ITEMS = [
  {
    id: 'summary',
    title: 'AthenaSummary',
    description: 'Topkwaliteit samenvattingen voor alle studies',
    href: 'https://athenasummary.nl/nl',
    image: IMAGES.summaryBooks,
    imageAlt: 'AthenaSummary samenvattingen',
  },
  {
    id: 'docs',
    title: 'AthenaDocs',
    description: 'Studeer vanaf dag 1 in de interactieve leeromgeving',
    href: 'https://athenadocs.nl/',
    mockup: true,
    imageAlt: 'AthenaDocs leeromgeving',
  },
];

const ICONS = {
  document: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="13" y2="17" />
    </svg>
  ),
  notebook: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  target: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1" />
    </svg>
  ),
  checklist: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
};

const PRODUCT_ITEMS_LEFT = [
  {
    id: 'literatuur',
    title: 'Literatuursamenvatting',
    description: 'Alle voorgeschreven literatuur van jouw vak, samengevat.',
    icon: ICONS.document,
  },
  {
    id: 'hoorcollege',
    title: 'Hoorcollege- en werkgroepsamenvatting',
    description: 'Verdieping en nadere uitleg van de onderwerpen die jouw vakdocenten écht belangrijk vinden.',
    icon: ICONS.notebook,
  },
];

const PRODUCT_ITEMS_RIGHT = [
  {
    id: 'examguide',
    title: 'Exam Guide',
    description: 'Stapsgewijze antwoordtechnieken, voorbeeldvragen en essentiële uitleg per leerstuk, voor 100% van de punten.',
    icon: ICONS.target,
  },
  {
    id: 'oefenvragen',
    title: 'Oefenvragen en tentamens',
    description: 'Test je kennis in AthenaDocs met oefenvragen en directe feedback over waarom antwoorden goed of fout zijn.',
    icon: ICONS.checklist,
  },
];

const PREMIUM_BENEFITS = [
  'Al je literatuur samengevat',
  'Stappenplannen voor elk leerstuk',
  'Uitgewerkte arresten',
  'Oefenvragen met directe feedback',
  'Wekelijks geüpdatet in AthenaDocs',
];

const STATS = [
  { id: 'rating', label: '★ rating', target: 9.17, decimals: 2, suffix: ' / 10', description: 'Gemiddeld cijfer dat jullie ons geven. Op weg naar de 10.' },
  { id: 'catalogus', label: 'catalogus', target: 2100, decimals: 0, plus: true, description: 'Samenvattingen beschikbaar. Voor (bijna) elke studie.' },
  { id: 'studenten', label: 'studenten', target: 59341, decimals: 0, description: 'Studenten haalden hun tentamen met Athena.' },
];

// FAQ-items. 'answer' kan een string zijn of JSX (voor de mailto-link).
const FAQ_ITEMS = [
  {
    q: 'Wat maakt de docenten bij AthenaStudies bijzonder?',
    a: (
      <>
        Onze docenten zijn topstudenten die zelf hoge cijfers hebben behaald in de vakken waarin ze lesgeven. Deze studenten worden zorgvuldig geselecteerd via een strenge interviewprocedure om ervoor te zorgen dat ze zowel academisch als didactisch uitmuntend zijn.
        <br /><br />
        Na hun selectie ondergaan onze docenten een uitgebreide training. Deze training omvat een algemene sessie waarin didactische en pedagogische principes worden aangeleerd, gevolgd door persoonlijke één-op-één sessies. In deze sessies worden hun prestaties geëvalueerd en verbeterd, met specifieke aandacht voor hun aanpak in hun vakgebied.
        <br /><br />
        Na elke training ontvangen jullie als studenten een evaluatieformulier om de docent te beoordelen. We hechten veel waarde aan deze evaluaties en gebruiken de feedback om onze docenten verder op te leiden en onze producten continu te verbeteren.
      </>
    ),
  },
  {
    q: 'Wat voor lesmateriaal is er bij de tentamentraining inbegrepen?',
    a: 'Bij de tentamentraining ontvang je een digitale samenvatting van AthenaSummary en overzichtelijke slides met vragen gebaseerd op eerdere tentamens.',
  },
  {
    q: 'Worden er ook tentamentrainingen voor de herkansingen gegeven?',
    a: (
      <>
        Ja, deze zullen uiterlijk in het begin van de periode waarin de herkansing plaatsvindt bekend worden en op onze website gepubliceerd worden. Staat jouw training er niet tussen, stuur dan een mailtje naar{' '}
        <Box component="a" href="mailto:info@athenastudies.nl" sx={{ color: COLORS.orange, textDecoration: 'underline' }}>info@athenastudies.nl</Box>
        . Bij voldoende vraag kunnen we deze training alsnog organiseren.
      </>
    ),
  },
];

/* ────────────────────────────────────────────────────────────
   4) HERBRUIKBARE STYLED-COMPONENTEN
   ──────────────────────────────────────────────────────────── */
// Organische 'blob'-hoekvormen (optie D). Twee varianten die elkaar afwisselen.
const BLOB_A = '60px 30px 55px 35px / 35px 55px 30px 60px';
const BLOB_B = '30px 60px 35px 55px / 55px 35px 60px 30px';
const BLOB_A_SM = '38px 20px 34px 22px / 22px 34px 20px 38px';
const BLOB_B_SM = '20px 38px 22px 34px / 34px 22px 38px 20px';

const Panel = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'blob',
})(({ theme, blob }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: blob === 'b' ? BLOB_B : blob === 'a' ? BLOB_A : 28,
  padding: theme.spacing(6),
  border: `1px solid ${COLORS.panelBorder}`,
  boxShadow: '0 24px 60px rgba(40,15,45,0.18)',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(2.5),
    borderRadius: blob === 'b' ? BLOB_B_SM : blob === 'a' ? BLOB_A_SM : 20,
  },
}));

const Pill = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 16px',
  borderRadius: 999,
  backgroundColor: COLORS.orangeSoft,
  border: `1px solid ${COLORS.orangeBorder}`,
  color: COLORS.orange,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
});

const ProductInfoCard = styled('div')(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 14,
  backgroundColor: COLORS.cardBg,
  border: `1px solid ${COLORS.cardBorder}`,
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  transition: 'transform .25s ease, background-color .25s ease, border-color .25s ease, box-shadow .25s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    backgroundColor: COLORS.cardBgHover,
    borderColor: COLORS.orangeBorder,
    boxShadow: '0 16px 34px rgba(0,0,0,0.28)',
  },
  '&:hover .product-icon': { transform: 'scale(1.08)' },
}));

const SmallIconBadge = styled('span')({
  flex: '0 0 auto',
  width: 34,
  height: 34,
  borderRadius: 9,
  backgroundColor: COLORS.orangeSoft,
  color: COLORS.orange,
  display: 'grid',
  placeItems: 'center',
});

const CheckBadge = styled('span')({
  flex: '0 0 auto',
  width: 24,
  height: 24,
  borderRadius: '50%',
  backgroundColor: COLORS.orange,
  color: COLORS.white,
  display: 'grid',
  placeItems: 'center',
});

const FaqItem = styled('div')({
  borderRadius: 14,
  backgroundColor: COLORS.lightCardBg,
  border: `1px solid ${COLORS.lightCardBorder}`,
  boxShadow: '0 4px 14px rgba(80,36,79,0.06)',
  overflow: 'hidden',
  transition: 'box-shadow .25s ease',
  '&:hover': { boxShadow: '0 8px 22px rgba(80,36,79,0.12)' },
});

const panelBackground = (variant) => {
  const glows = {
    a: `radial-gradient(60% 80% at 82% 16%, ${COLORS.glowPurple} 0%, transparent 60%), radial-gradient(55% 70% at 10% 95%, ${COLORS.glowOrange} 0%, transparent 55%)`,
    b: `radial-gradient(55% 70% at 88% 12%, rgba(150,80,150,0.5) 0%, transparent 55%), radial-gradient(50% 60% at 8% 95%, ${COLORS.glowOrange} 0%, transparent 55%)`,
    c: `radial-gradient(50% 80% at 45% 20%, ${COLORS.glowPurple} 0%, transparent 55%), radial-gradient(45% 60% at 95% 90%, ${COLORS.glowOrange} 0%, transparent 55%)`,
    d: `radial-gradient(55% 70% at 12% 18%, rgba(150,80,150,0.5) 0%, transparent 55%), radial-gradient(45% 60% at 92% 92%, ${COLORS.glowOrange} 0%, transparent 55%)`,
    e: `radial-gradient(50% 70% at 90% 80%, rgba(150,80,150,0.5) 0%, transparent 55%), radial-gradient(45% 60% at 8% 15%, ${COLORS.glowOrange} 0%, transparent 55%)`,
    f: `radial-gradient(50% 70% at 50% 8%, ${COLORS.glowPurple} 0%, transparent 55%), radial-gradient(45% 60% at 95% 95%, ${COLORS.glowOrange} 0%, transparent 55%)`,
  };
  return `${glows[variant]}, linear-gradient(135deg, ${COLORS.bgFrom} 0%, ${COLORS.bgMid} 55%, ${COLORS.bgTo} 100%)`;
};

/* ────────────────────────────────────────────────────────────
   5) HULP-COMPONENTEN (interactief)
   ──────────────────────────────────────────────────────────── */

// Telt omhoog naar 'target' zodra het in beeld komt.
function StatCounter({ target, decimals = 0, suffix = '', plus = false }) {
  const [value, setValue] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const duration = 1500;
            let startTime = null;
            const step = (ts) => {
              if (startTime === null) startTime = ts;
              const p = Math.min((ts - startTime) / duration, 1);
              const eased = 1 - Math.pow(1 - p, 3);
              setValue(target * eased);
              if (p < 1) requestAnimationFrame(step);
              else setValue(target);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target]);

  const formatted = value.toLocaleString('nl-NL', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <Box component="span" ref={ref} sx={{ fontWeight: 800, color: COLORS.deepPurple, fontSize: { xs: 26, sm: 32, md: 44 }, lineHeight: 1 }}>
      {formatted}
      {plus && <Box component="span" sx={{ color: COLORS.orange }}>+</Box>}
      {suffix && <Box component="span" sx={{ fontSize: { xs: 13, sm: 16, md: 20 }, color: '#A98BA8', fontWeight: 700 }}>{suffix}</Box>}
    </Box>
  );
}

function DocsMiniMockup() {
  const rows = [
    { label: 'Samenvatting', meta: '74 pag' },
    { label: 'Stappenplan', meta: '6 stappen' },
    { label: 'Oefenvragen', meta: '24 vragen' },
  ];
  return (
    <Box sx={{ width: '100%', height: '100%', borderRadius: 2, overflow: 'hidden', background: 'linear-gradient(180deg, #2a1230, #1f0d24)', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 8px 20px rgba(40,15,45,0.3)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.375, px: 0.75, py: 0.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: '#e0564f' }} />
        <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: '#e8a13c' }} />
        <Box sx={{ width: 4, height: 4, borderRadius: '50%', background: '#5fb86a' }} />
        <Box sx={{ ml: 'auto', px: 0.5, borderRadius: 0.5, background: COLORS.orange, color: '#fff', fontSize: 5.5, fontWeight: 700, lineHeight: 1.4 }}>PRO</Box>
      </Box>
      <Box sx={{ flex: 1, m: 0.5, p: 0.625, borderRadius: 1, background: '#fff', display: 'flex', flexDirection: 'column', gap: 0.375, minWidth: 0 }}>
        <Box component="span" sx={{ alignSelf: 'flex-start', px: 0.5, borderRadius: 0.5, background: 'rgba(245,135,31,0.14)', color: COLORS.orangeInk, fontSize: 5.5, fontWeight: 700, lineHeight: 1.5, letterSpacing: '0.04em' }}>BESTUURSRECHT</Box>
        {rows.map((r) => (
          <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Typography sx={{ color: COLORS.deepPurple, fontSize: 6, fontWeight: 600, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: '1 1 auto', minWidth: 0 }}>{r.label}</Typography>
            <Typography sx={{ color: COLORS.lightTextMuted, fontSize: 5.5, lineHeight: 1.3, flex: '0 0 auto', whiteSpace: 'nowrap' }}>{r.meta}</Typography>
          </Box>
        ))}
        <Box sx={{ mt: 'auto', height: 3, borderRadius: 2, background: 'rgba(80,36,79,0.12)' }}>
          <Box sx={{ width: '67%', height: '100%', borderRadius: 2, background: COLORS.orange }} />
        </Box>
      </Box>
    </Box>
  );
}

function DocsMockup({ compact = false }) {
  const rows = [
    { icon: '📄', label: 'Samenvatting', meta: '74 pag' },
    { icon: '⚖️', label: 'HR 2024 · Awb art. 1:3', meta: 'arrest' },
    { icon: '🗂️', label: 'Stappenplan bezwaar', meta: '6 stappen' },
    { icon: '❓', label: 'Oefenvragen', meta: '24 vragen' },
  ];
  return (
    <Box
      sx={{
        position: 'relative', width: '100%', maxWidth: compact ? 300 : 420,
        borderRadius: 3, overflow: 'hidden',
        background: 'linear-gradient(180deg, #2a1230, #1f0d24)',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 18px 40px rgba(0,0,0,0.4)',
      }}
    >
      {/* Titelbalk */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.75, py: 1.25, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <Box sx={{ display: 'flex', gap: 0.625 }}>
          <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#e0564f' }} />
          <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#e8a13c' }} />
          <Box sx={{ width: 9, height: 9, borderRadius: '50%', background: '#5fb86a' }} />
        </Box>
        <Typography sx={{ ml: 0.5, color: 'rgba(255,255,255,0.85)', fontSize: 12.5, fontWeight: 600 }}>AthenaDocs · Premium</Typography>
        <Box sx={{ ml: 'auto', px: 1, py: 0.25, borderRadius: 1, background: COLORS.orange, color: '#fff', fontSize: 10, fontWeight: 700, letterSpacing: '0.05em' }}>PRO</Box>
      </Box>
      {/* Witte kaart */}
      <Box sx={{ m: 1.5, p: 1.75, borderRadius: 2.5, background: '#fff' }}>
        <Box component="span" sx={{ display: 'inline-block', px: 1, py: 0.375, borderRadius: 1, background: 'rgba(245,135,31,0.14)', color: COLORS.orangeInk, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em' }}>BESTUURSRECHT</Box>
        <Typography sx={{ mt: 1, color: COLORS.deepPurple, fontSize: 14.5, fontWeight: 700 }}>Hoofdstuk 4 · Bezwaar &amp; beroep</Typography>
        <Box sx={{ mt: 1.25, display: 'flex', flexDirection: 'column', gap: 0.875 }}>
          {rows.map((r) => (
            <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 1, borderRadius: 1.5, background: '#F6F2F6' }}>
              <Box component="span" sx={{ fontSize: 13 }}>{r.icon}</Box>
              <Typography sx={{ color: COLORS.deepPurple, fontSize: 12.5, fontWeight: 600 }}>{r.label}</Typography>
              <Typography sx={{ ml: 'auto', color: COLORS.lightTextMuted, fontSize: 11.5 }}>{r.meta}</Typography>
            </Box>
          ))}
        </Box>
        {/* Voortgang */}
        <Box sx={{ mt: 1.5, pt: 1.25, borderTop: '1px solid rgba(80,36,79,0.1)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ color: COLORS.deepPurple, fontSize: 12, fontWeight: 600 }}>Voortgang hoofdstuk</Typography>
            <Typography sx={{ ml: 'auto', color: COLORS.orangeInk, fontSize: 12, fontWeight: 700 }}>12 / 18 · 67%</Typography>
          </Box>
          <Box sx={{ mt: 0.75, height: 6, borderRadius: 3, background: 'rgba(80,36,79,0.1)', overflow: 'hidden' }}>
            <Box sx={{ width: '67%', height: '100%', borderRadius: 3, background: COLORS.orange }} />
          </Box>
        </Box>
      </Box>
      {/* Editie-label */}
      <Box sx={{ position: 'absolute', bottom: 10, right: 10, px: 1.25, py: 0.5, borderRadius: 1.5, background: COLORS.orange, color: '#fff', fontSize: 10.5, fontWeight: 700 }}>Editie 2025/2026</Box>
    </Box>
  );
}

function ProductCardView({ item }) {
  return (
    <ProductInfoCard>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <SmallIconBadge className="product-icon" sx={{ transition: 'transform .25s ease' }}>{item.icon}</SmallIconBadge>
        <Typography sx={{ color: COLORS.white, fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>{item.title}</Typography>
      </Box>
      <Typography sx={{ mt: 1, color: COLORS.subtext, fontSize: 13.5, lineHeight: 1.45 }}>{item.description}</Typography>
    </ProductInfoCard>
  );
}

function FaqAccordion({ items }) {
  const [openSet, setOpenSet] = useState(() => new Set());
  const toggle = useCallback((index) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {items.map((item, index) => {
        const isOpen = openSet.has(index);
        return (
          <FaqItem key={item.q}>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => toggle(index)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(index); } }}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, p: '18px 20px', cursor: 'pointer', color: COLORS.deepPurple, fontWeight: 700, fontSize: 15.5 }}
            >
              <span>{item.q}</span>
              <Box
                component="span"
                sx={{
                  position: 'relative', flex: '0 0 auto', width: 24, height: 24, borderRadius: '50%',
                  backgroundColor: COLORS.orange, display: 'grid', placeItems: 'center',
                  '&::before, &::after': { content: '""', position: 'absolute', backgroundColor: '#fff', borderRadius: '2px' },
                  '&::before': { width: 11, height: 2.4 },
                  '&::after': { width: 2.4, height: 11, transition: 'transform .3s ease', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' },
                }}
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateRows: isOpen ? '1fr' : '0fr', transition: 'grid-template-rows .3s ease' }}>
              <Box sx={{ overflow: 'hidden' }}>
                <Typography component="div" sx={{ p: '0 20px 18px', color: COLORS.lightTextMuted, fontSize: 14, lineHeight: 1.6 }}>
                  {item.a}
                </Typography>
              </Box>
            </Box>
          </FaqItem>
        );
      })}
    </Box>
  );
}

/* ────────────────────────────────────────────────────────────
   6) COMPONENT
   ──────────────────────────────────────────────────────────── */
export default function MeerVanAthena({
  introBadge = 'Over ons',
  introHeading = 'Athena: jouw partner in succesvol studeren',
  introParagraphs = [
    'Als onderdeel van de Pallas Athena group streven wij sinds 2014 naar topkwaliteit studieondersteuning, essentieel voor een succesvolle studieverloop.',
    'Voorbereiden op tentamens is een uitdaging. Daarom bieden we beknopte en tentamengerichte producten zoals samenvattingen, Exam Guides en oefenvragen waarmee jij het tentamen gewoon gaat halen.',
  ],
  introImageLabel = 'Sinds 2012',
  familieBadge = 'Athena familie',
  familieHeading = 'Meer van Athena',
  familieSubtitle = 'Ontdek de rest van de Athena-familie, alles om jouw tentamen te halen.',
  familieItems = FAMILIE_ITEMS,
  productenBadge = 'Wat wij maken',
  productenHeading = 'De producten van AthenaSummary',
  productenSubtitle = 'De bouwstenen voor jouw tentamen, gecontroleerd door topstudenten.',
  bridgeLabel = 'Alles samen op één plek in',
  premiumBadge = 'De complete bundel',
  premiumHeading = 'AthenaDocs Premium',
  premiumSubheading = 'Voor rechtsgeleerdheid',
  premiumDescription = 'Alle producten hierboven, samengebracht in één interactieve leeromgeving. Stappenplannen, uitgewerkte arresten en oefenvragen, tot in detail uitgewerkt voor die 10.',
  premiumBenefits = PREMIUM_BENEFITS,
  schermenBadge = 'Achter de schermen bij AthenaSummary',
  schermenHeading = 'Een samenvatting zegt meer dan duizend bladzijden',
  schermenDescription = 'De epische reis die jouw samenvatting aflegt voordat wij gezamenlijk naar die 10 werken. Alle tentamenstof. Gewoon samengevat.',
  cijfersBadge = 'Onze cijfers',
  cijfersHeading = 'Nu gaan we die van jou fixen.',
  stats = STATS,
  faqBadge = 'FAQ',
  faqHeading = 'Veelgestelde vragen',
  faqItems = FAQ_ITEMS,
} = {}) {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <Box component="section" className="meer-van-athena" sx={{ backgroundColor: 'transparent', pt: 0, pb: { xs: 3.5, md: 7 }, px: { xs: 1.5, md: 3 } }}>
      <Container maxWidth="lg" disableGutters>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 4 } }}>

          {/* SECTIE 1: MEER VAN ATHENA (licht; steekt als kaart over de paarse hero heen) */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 5,
              // Steekt over de onderrand van de aurora-hero (MUI-spacing = px/8).
              // -10 = 80px (desktop), -5.5 = 44px (mobiel).
              mt: { xs: -5.5, md: -10 },
              overflow: 'hidden',
              borderRadius: { xs: '20px', md: '28px' },
              p: { xs: 2.5, md: 4 },
              background: 'linear-gradient(135deg, #F1EAF3 0%, #F7EEE8 55%, #FBEAE0 100%)',
              border: '1px solid rgba(80,36,79,0.07)',
              boxShadow: '0 24px 60px rgba(40,15,45,0.35)',
            }}
          >
            <Box aria-hidden sx={{ position: 'absolute', top: -70, right: -50, width: 260, height: 260, borderRadius: '50%', background: 'rgba(245,135,31,0.08)', pointerEvents: 'none' }} />
            <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', mb: { xs: 2, md: 2.5 } }}>
              {familieBadge && <Pill sx={{ background: 'rgba(245,135,31,0.12)', borderColor: 'rgba(245,135,31,0.45)', color: COLORS.orangeInk }}>+ {familieBadge}</Pill>}
              <Typography component="h2" sx={{ mt: 1.5, fontWeight: 800, color: COLORS.familieHeading2, fontSize: { xs: 23, md: 28 }, lineHeight: 1.15 }}>{familieHeading}</Typography>
              {familieSubtitle && <Typography sx={{ mt: 1, mx: 'auto', maxWidth: 520, color: COLORS.familieSubtext, fontSize: { xs: 13.5, md: 15 }, lineHeight: 1.45 }}>{familieSubtitle}</Typography>}
            </Box>
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch', gap: { xs: 1.5, md: 2.5 } }}>
              {familieItems.map((item) => (
                <Card
                  key={item.id}
                  component="a"
                  href={item.href}
                  className="meer-van-athena__card"
                  sx={{
                    flex: { xs: '0 0 auto', md: '1 1 0' }, width: { xs: '100%', md: 'auto' }, minWidth: 0, display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 },
                    p: { xs: 1.5, md: 1.75 }, borderRadius: '16px',
                    backgroundColor: COLORS.familieCardBg,
                    border: `1px solid ${COLORS.familieCardBorder}`,
                    backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                    textDecoration: 'none',
                    transition: 'transform .25s ease, box-shadow .25s ease, background-color .25s ease, border-color .25s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      backgroundColor: COLORS.familieCardBgHover,
                      borderColor: COLORS.orangeBorder,
                      boxShadow: '0 16px 36px rgba(80,36,79,0.16)',
                    },
                    '&:hover .mva-chevron': { transform: 'translateX(4px)' },
                  }}
                >
                  <Box
                    sx={{
                      flex: '0 0 auto', width: { xs: 84, sm: 116 }, height: { xs: 72, sm: 88 },
                      borderRadius: '12px', display: 'grid', placeItems: 'center', overflow: 'hidden', boxSizing: 'border-box',
                      ...(item.mockup
                        ? { p: 0, background: 'transparent', border: 'none' }
                        : { p: { xs: '7px', sm: '9px' }, backgroundColor: COLORS.familieImgBg, border: `1px solid ${COLORS.familieImgBorder}` }),
                    }}
                  >
                    {item.mockup
                      ? <DocsMiniMockup />
                      : (item.image ? <Box component="img" src={item.image} alt={item.imageAlt} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} /> : null)}
                  </Box>
                  <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: COLORS.familieHeading2, fontSize: { xs: 16.5, md: 18 }, lineHeight: 1.25 }}>{item.title}</Typography>
                    <Typography sx={{ mt: 0.5, color: COLORS.familieSubtext, fontSize: { xs: 13, md: 13.5 }, lineHeight: 1.4 }}>{item.description}</Typography>
                  </Box>
                  <Box className="mva-chevron" sx={{ flex: '0 0 auto', display: 'grid', placeItems: 'center', color: COLORS.orange, transition: 'transform .25s ease' }}>
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                  </Box>
                </Card>
              ))}
            </Box>
          </Box>

          {/* SECTIE 2 + 3: PRODUCTEN (bouwstenen) -> ATHENADOCS PREMIUM (bundel), verbonden */}
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>

            {/* SECTIE 2: ONZE PRODUCTEN (de bouwstenen) */}
            <Panel sx={{ background: panelBackground('b') }}>
              <Box aria-hidden sx={{ position: 'absolute', top: -60, right: -40, width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

              {/* Kop: op mobiel boeken naast de tekst, op desktop gecentreerd */}
              <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'row', md: 'column' }, alignItems: 'center', textAlign: { xs: 'left', md: 'center' }, gap: { xs: 2, md: 0 }, mb: { xs: 2.5, md: 4 } }}>
                <Box sx={{ flex: { xs: '1 1 auto', md: 'unset' }, minWidth: 0 }}>
                  {productenBadge && <Pill>+ {productenBadge}</Pill>}
                  <Typography component="h2" sx={{ mt: { xs: 1.25, md: 2 }, fontWeight: 800, color: COLORS.white, fontSize: { xs: 21, md: 30 }, lineHeight: 1.15 }}>{productenHeading}</Typography>
                  {productenSubtitle && <Typography sx={{ mt: { xs: 1, md: 1.5 }, mx: { xs: 0, md: 'auto' }, maxWidth: 560, color: COLORS.subtext, fontSize: { xs: 13, md: 15 }, lineHeight: 1.45 }}>{productenSubtitle}</Typography>}
                </Box>
                {IMAGES.productBooks && (
                  <Box component="img" src={IMAGES.productBooks} alt="AthenaSummary boeken" sx={{ display: { xs: 'block', md: 'none' }, flex: '0 0 auto', width: 96, maxHeight: 130, objectFit: 'contain', filter: 'drop-shadow(0 12px 22px rgba(0,0,0,0.35))' }} />
                )}
              </Box>

              {/* Kaarten: op mobiel één kolom, op desktop twee kolommen met boeken ertussen */}
              <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: { xs: 1.5, md: 3 } }}>
                <Box sx={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
                  {PRODUCT_ITEMS_LEFT.map((item) => <ProductCardView key={item.id} item={item} />)}
                </Box>
                <Box sx={{ flex: { md: '0 0 180px' }, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                  {IMAGES.productBooks ? <Box component="img" src={IMAGES.productBooks} alt="AthenaSummary boeken" sx={{ maxWidth: '100%', maxHeight: 260, objectFit: 'contain', display: 'block', filter: 'drop-shadow(0 18px 30px rgba(0,0,0,0.35))' }} /> : null}
                </Box>
                <Box sx={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 1.5, md: 2 } }}>
                  {PRODUCT_ITEMS_RIGHT.map((item) => <ProductCardView key={item.id} item={item} />)}
                </Box>
              </Box>
            </Panel>

            {/* BRUG: verbindt de bouwstenen met de Premium-bundel */}
            {bridgeLabel && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 0.5 }}>
                <Box aria-hidden sx={{ width: '2px', height: 18, background: `linear-gradient(${COLORS.orange}, rgba(245,135,31,0.3))` }} />
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.875, borderRadius: 999, backgroundColor: '#fff', border: '1px solid rgba(245,135,31,0.35)', boxShadow: '0 6px 18px rgba(80,36,79,0.12)', color: COLORS.deepPurple, fontSize: 12.5, fontWeight: 700 }}>
                  <Box component="span" sx={{ color: COLORS.orangeInk, display: 'grid', placeItems: 'center' }}>
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><polyline points="19 12 12 19 5 12" /></svg>
                  </Box>
                  {bridgeLabel}
                </Box>
                <Box aria-hidden sx={{ width: '2px', height: 18, background: `linear-gradient(rgba(245,135,31,0.3), ${COLORS.orange})` }} />
              </Box>
            )}

            {/* SECTIE 3: ATHENADOCS PREMIUM (de complete bundel, onderscheidend) */}
            <Panel
              sx={{
                border: `1.5px solid ${COLORS.green}`,
                boxShadow: '0 24px 56px rgba(34,165,101,0.20), 0 0 0 4px rgba(34,165,101,0.06)',
                background: `radial-gradient(60% 80% at 18% 12%, rgba(34,165,101,0.34) 0%, transparent 55%), radial-gradient(50% 70% at 95% 95%, rgba(214,83,126,0.32) 0%, transparent 55%), linear-gradient(135deg, #2c4a39 0%, ${COLORS.bgMid} 55%, ${COLORS.bgFrom} 100%)`,
              }}
            >
              {/* groen 'PREMIUM'-lint, correct over de rechterbovenhoek */}
              <Box aria-hidden sx={{ position: 'absolute', top: 26, right: -52, width: 200, transform: 'rotate(45deg)', textAlign: 'center', background: `linear-gradient(135deg, ${COLORS.green}, ${COLORS.greenInk})`, color: '#fff', fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', py: 0.75, boxShadow: '0 4px 14px rgba(0,0,0,0.35)', zIndex: 3 }}>PREMIUM</Box>

              <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: { xs: 3, md: 5 } }}>
                <Box sx={{ flex: 1, width: '100%' }}>
                  {premiumBadge && <Pill sx={{ background: 'rgba(245,135,31,0.22)', borderColor: 'rgba(245,135,31,0.7)' }}>★ {premiumBadge}</Pill>}
                  <Typography component="h2" sx={{ mt: 2, fontWeight: 800, color: COLORS.white, fontSize: { xs: 26, md: 32 }, lineHeight: 1.1 }}>{premiumHeading}</Typography>
                  {premiumSubheading && <Typography sx={{ mt: 0.75, color: COLORS.orange, fontSize: 12.5, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{premiumSubheading}</Typography>}
                  {premiumDescription && <Typography sx={{ mt: 2, color: COLORS.subtext, fontSize: { xs: 14, md: 15 }, lineHeight: 1.5 }}>{premiumDescription}</Typography>}
                  <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {premiumBenefits.map((benefit) => (
                      <Box key={benefit} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CheckBadge>
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        </CheckBadge>
                        <Typography sx={{ color: COLORS.listText, fontSize: 14.5 }}>{benefit}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box sx={{ flex: { xs: '1 1 auto', md: '0 0 380px' }, display: 'flex', justifyContent: 'center', width: '100%' }}>
                  <DocsMockup />
                </Box>
              </Box>
            </Panel>

          </Box>

          {/* SECTIE 4: ONZE CIJFERS (licht, compacte band met count-up) */}
          <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: { xs: '18px', md: '28px' }, p: { xs: 2.5, md: 4 }, backgroundColor: COLORS.lightCardBg, border: `1px solid ${COLORS.lightCardBorder}`, boxShadow: '0 12px 36px rgba(80,36,79,0.08)' }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 2, md: 3 } }}>
              {cijfersBadge && (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.75, py: 0.75, borderRadius: 999, backgroundColor: 'rgba(245,135,31,0.12)', border: '1px solid rgba(245,135,31,0.4)', color: COLORS.orangeInk, fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' }}>+ {cijfersBadge}</Box>
              )}
              <Typography component="h2" sx={{ mt: 1.5, fontWeight: 800, color: COLORS.deepPurple, fontSize: { xs: 20, md: 28 }, lineHeight: 1.15 }}>{cijfersHeading}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
              {stats.map((stat, index) => (
                <React.Fragment key={stat.id}>
                  {index > 0 && (
                    <Box aria-hidden sx={{ flex: '0 0 1px', alignSelf: 'stretch', my: { xs: 0.5, md: 1 }, background: `linear-gradient(180deg, transparent, ${COLORS.lightDivider}, transparent)` }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', px: { xs: 0.75, md: 2 } }}>
                    <Typography sx={{ color: COLORS.orangeInk, fontSize: { xs: 9.5, md: 11 }, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.2 }}>{stat.label}</Typography>
                    <Box sx={{ mt: { xs: 0.75, md: 1 } }}>
                      <StatCounter target={stat.target} decimals={stat.decimals} suffix={stat.suffix} plus={stat.plus} />
                    </Box>
                    <Typography sx={{ display: { xs: 'none', sm: 'block' }, mt: 1, mx: 'auto', maxWidth: 240, color: COLORS.lightTextMuted, fontSize: { sm: 12, md: 13 }, lineHeight: 1.4 }}>{stat.description}</Typography>
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          </Box>

          {/* SECTIE 5: ACHTER DE SCHERMEN (video lightbox) */}
          <Panel sx={{ background: panelBackground('d') }}>
            <Box aria-hidden sx={{ position: 'absolute', bottom: -60, left: -40, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: { xs: 3, md: 5 } }}>
              <Box sx={{ flex: 1, width: '100%' }}>
                {schermenBadge && <Pill>+ {schermenBadge}</Pill>}
                <Typography component="h2" sx={{ mt: 2, fontWeight: 800, color: COLORS.white, fontSize: { xs: 24, md: 30 }, lineHeight: 1.15 }}>{schermenHeading}</Typography>
                {schermenDescription && <Typography sx={{ mt: 2, color: COLORS.subtext, fontSize: { xs: 14, md: 15 }, lineHeight: 1.5 }}>{schermenDescription}</Typography>}
              </Box>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setVideoOpen(true)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setVideoOpen(true); } }}
                sx={{
                  flex: { xs: '1 1 auto', md: '0 0 360px' }, width: '100%', position: 'relative', cursor: 'pointer',
                  borderRadius: '16px', overflow: 'hidden', border: `2px solid ${COLORS.orangeBorder}`,
                  transition: 'transform .25s ease, box-shadow .25s ease',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 18px 36px rgba(0,0,0,0.35)' },
                  '&:hover .play-btn': { transform: 'translate(-50%,-50%) scale(1.12)' },
                }}
              >
                <Box component="img" src={VIDEO.thumb} alt={schermenHeading} sx={{ display: 'block', width: '100%', height: { xs: 200, md: 210 }, objectFit: 'cover' }} />
                <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(58,24,56,0.08), rgba(58,24,56,0.35))' }} />
                <Box className="play-btn" sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 60, height: 60, borderRadius: '50%', background: 'rgba(255,255,255,0.92)', display: 'grid', placeItems: 'center', transition: 'transform .25s ease' }}>
                  <Box sx={{ width: 0, height: 0, borderLeft: `18px solid ${COLORS.bgMid}`, borderTop: '11px solid transparent', borderBottom: '11px solid transparent', ml: '4px' }} />
                </Box>
                <Box sx={{ position: 'absolute', bottom: 12, left: 12, background: COLORS.green, color: '#fff', fontSize: 11, fontWeight: 700, px: 1.25, py: 0.5, borderRadius: 1.5, boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>{VIDEO.label}</Box>
              </Box>
            </Box>
          </Panel>

          {/* SECTIE: INTRO / OVER ONS (onder Onze cijfers) */}
          <Panel sx={{ background: panelBackground('d') }}>
            <Box aria-hidden sx={{ position: 'absolute', top: -70, left: -50, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', gap: { xs: 3, md: 5 } }}>
              <Box sx={{ flex: 1, width: '100%' }}>
                {introBadge && <Pill>+ {introBadge}</Pill>}
                <Typography component="h2" sx={{ mt: 2, fontWeight: 800, color: COLORS.white, fontSize: { xs: 26, md: 32 }, lineHeight: 1.15 }}>{introHeading}</Typography>
                {introParagraphs.map((paragraph, i) => (
                  <Typography key={i} sx={{ mt: 2, color: COLORS.subtext, fontSize: { xs: 14, md: 15 }, lineHeight: 1.55 }}>{paragraph}</Typography>
                ))}
              </Box>
              <Box sx={{ flex: { xs: '1 1 auto', md: '0 0 440px' }, width: '100%', position: 'relative', borderRadius: { xs: 3, md: '20px' }, overflow: 'hidden', boxShadow: '0 18px 40px rgba(0,0,0,0.35)' }}>
                {IMAGES.team ? (
                  <Box component="img" src={IMAGES.team} alt="Het team van AthenaSummary" sx={{ display: 'block', width: '100%', height: { xs: 240, md: 300 }, objectFit: 'cover' }} />
                ) : (
                  <Box sx={{ height: { xs: 240, md: 300 }, background: 'rgba(255,255,255,0.06)' }} />
                )}
                {introImageLabel && (
                  <Box sx={{ position: 'absolute', bottom: 12, right: 12, background: COLORS.orange, color: '#fff', fontSize: 12, fontWeight: 600, px: 1.5, py: 0.75, borderRadius: 2 }}>{introImageLabel}</Box>
                )}
              </Box>
            </Box>
          </Panel>

          {/* SECTIE 6: FAQ (licht, witte kaarten, meerdere tegelijk open) */}
          <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: { xs: '20px', md: '28px' }, p: { xs: 3, md: 6 }, background: 'linear-gradient(180deg, #FBF8FB, #F4F4F6)', border: `1px solid rgba(80,36,79,0.06)` }}>
            <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
              {faqBadge && (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 2, py: 0.875, borderRadius: 999, backgroundColor: 'rgba(245,135,31,0.12)', border: '1px solid rgba(245,135,31,0.4)', color: COLORS.orangeInk, fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' }}>+ {faqBadge}</Box>
              )}
              <Typography component="h2" sx={{ mt: 2, fontWeight: 800, color: COLORS.deepPurple, fontSize: { xs: 24, md: 30 }, lineHeight: 1.15 }}>{faqHeading}</Typography>
            </Box>
            <FaqAccordion items={faqItems} />
          </Box>

        </Box>
      </Container>

      {/* VIDEO LIGHTBOX */}
      <Modal open={videoOpen} onClose={() => setVideoOpen(false)} sx={{ display: 'grid', placeItems: 'center', p: 2 }}>
        <Box sx={{ position: 'relative', width: '100%', maxWidth: 900, outline: 'none' }}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setVideoOpen(false)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setVideoOpen(false); } }}
            sx={{ position: 'absolute', top: -44, right: 0, color: '#fff', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            Sluiten
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </Box>
          <Box sx={{ position: 'relative', pt: '56.25%', borderRadius: 14, overflow: 'hidden', boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}>
            {videoOpen && (
              <Box
                component="iframe"
                src={`https://www.youtube.com/embed/${VIDEO.youtubeId}?autoplay=1`}
                title="AthenaSummary video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              />
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}
